import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyProfile } from '../services/userProfileService';
import { getMyRatings } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './MyProfile.css';

const MyProfile = () => {
  const [profile, setProfile] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isLoggedIn, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isLoggedIn, authLoading, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profileData, ratingsData] = await Promise.all([
        getMyProfile(),
        getMyRatings(),
      ]);
      setProfile(profileData);
      setRatings(ratingsData || []);
      setError('');
    } catch (err) {
      setError('Failed to load your profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'pending':
        return 'status-pending';
      case 'confirmed':
        return 'status-confirmed';
      case 'checked_in':
        return 'status-checked-in';
      case 'cancelled':
        return 'status-cancelled';
      case 'no_show':
        return 'status-no-show';
      default:
        return 'status-pending';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="my-profile-page">
        <Navbar />
        <div className="profile-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading your profile...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-profile-page">
        <Navbar />
        <div className="profile-container">
          <div className="error-message">
            <i className="bi bi-exclamation-circle"></i>
            <p>{error}</p>
            <button onClick={fetchData} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="my-profile-page">
      <Navbar />

      <div className="profile-container">
        {/* Profile Header with gradient background */}
        <div className="profile-header">
          <div className="profile-header-content">
            <div className="profile-avatar">
              <i className="bi bi-person-circle"></i>
            </div>
            <div className="profile-info">
              <h1>
                {profile?.user?.firstName && profile?.user?.lastName
                  ? `${profile.user.firstName} ${profile.user.lastName}`
                  : 'My Profile'}
              </h1>
              <p className="profile-email">{profile?.user?.email}</p>
              {profile?.user?.phoneNumber && (
                <p className="profile-phone">
                  <i className="bi bi-telephone"></i> {profile.user.phoneNumber}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="profile-content">
          {/* Stats Section */}
          <div className="stats-section">
            <div className="stat-card">
              <div className="stat-icon">
                <i className="bi bi-calendar-check"></i>
              </div>
              <div className="stat-info">
                <span className="stat-value">{profile?.stats?.totalReservations || 0}</span>
                <span className="stat-label">Total Reservations</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon completed">
                <i className="bi bi-check-circle"></i>
              </div>
              <div className="stat-info">
                <span className="stat-value">{profile?.stats?.completedReservations || 0}</span>
                <span className="stat-label">Completed</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon upcoming">
                <i className="bi bi-clock"></i>
              </div>
              <div className="stat-info">
                <span className="stat-value">{profile?.stats?.upcomingReservations || 0}</span>
                <span className="stat-label">Upcoming</span>
              </div>
            </div>
          </div>

          {/* My Ratings Section */}
          <div className="ratings-section">
            <h2><i className="bi bi-star-fill me-2"></i>My Ratings</h2>
            <p className="section-description">
              Your reviews and ratings for restaurants you've visited.
            </p>

            {ratings && ratings.length > 0 ? (
              <div className="ratings-list">
                {ratings.map((rating) => (
                  <div key={rating.id} className={`rating-card ${!rating.comment ? 'no-comment' : ''}`}>
                    <div className="rating-header">
                      <div className="restaurant-info">
                        <i className="bi bi-shop"></i>
                        <h3>{rating.restaurantName}</h3>
                      </div>
                      <div className="rating-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i
                            key={star}
                            className={`bi ${star <= rating.stars ? 'bi-star-fill' : 'bi-star'}`}
                          ></i>
                        ))}
                      </div>
                    </div>

                    <div className="rating-body">
                      {rating.comment && (
                        <div className="rating-comment">
                          <p>{rating.comment}</p>
                        </div>
                      )}

                      <div className="rating-date">
                        <i className="bi bi-calendar3"></i>
                        <span>{formatDate(rating.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <i className="bi bi-star"></i>
                <p>You haven't rated any restaurants yet</p>
                <button onClick={() => navigate('/')} className="browse-btn">
                  Browse Restaurants
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MyProfile;
